import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  ElementRef,
  OnDestroy,
  HostListener,
  TemplateRef,
  OnChanges,
  SimpleChanges,
  AfterViewChecked
} from '@angular/core';
import rangePlugin from './sukaRangePlugin';
import flatpickr from 'flatpickr';
import { Spanish } from 'flatpickr/dist/l10n/es.js';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { sukaFlatpickrMonthSelectPlugin } from './sukaFlatpickrMonthSelectPlugin';


@Component({
  selector: 'suka-date-picker',
  template: `
    <div class="form-item">
      <div
        class="date-picker"
        [ngClass]="{
          'date-picker--range' : range,
          'date-picker--single' : !range,
          'date-picker--range-with-time' : range && hasTime,
          'skeleton' : skeleton
        }">
        <div class="date-picker-container">
          <suka-date-picker-input
            [label]="label"
            [placeholder]="placeholder"
            [pattern]="pattern"
            [id]="id"
            [type]="(range ? 'range' : 'single')"
            [hasIcon]="(range ? false : true)"
            [disabled]="disabled"
            [invalid]="invalid"
            [invalidText]="invalidText"
            [timePicker]="hasTime && noCalendar"
            [skeleton]="skeleton"
            (valueChange)="onValueChange($event)">
          </suka-date-picker-input>
        </div>
        <div *ngIf="range" class="date-picker-container">
          <suka-date-picker-input
            [label]="rangeLabel"
            [placeholder]="placeholder"
            [pattern]="pattern"
            [id]="id + '-rangeInput'"
            [type]="(range ? 'range' : 'single')"
            [hasIcon]="(range ? true : null)"
            [disabled]="disabled"
            [invalid]="invalid"
            [invalidText]="invalidText"
            [timePicker]="hasTime && noCalendar"
            [skeleton]="skeleton"
            (valueChange)="onRangeValueChange($event)">
          </suka-date-picker-input>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DatePicker,
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None
})
export class DatePicker implements OnDestroy, OnChanges, AfterViewChecked {
  private static datePickerCount = 0;

  /**
   * Select calendar range mode
   */
  @Input() range = false;

  /**
   * Format of date
   *
   * For reference: https://flatpickr.js.org/formatting/
   */
  @Input() dateFormat = 'd/m/Y';

  /**
   * Defines if time selector should be present. Please define dateFormat accordingly. i.e.: "d/m/Y H:i"
   *
   * For reference: https://flatpickr.js.org/formatting/
   */
  @Input() hasTime = false;

  /**
   * Disables calendar input. Useful for time picker functionality. Pair it with a time related format accordingly. i.e.: "H:i"
   */
  @Input() noCalendar = false;

  /**
   * Date picker label.
   */
  @Input() label: string | TemplateRef<any>;

  /**
   * Label for date range selector.
   */
  @Input() rangeLabel: string;

  /**
   * Sets the placeholder text.
   */
  @Input() placeholder = 'dd/mm/yyyy';

  /**
   * Sets the pattern to follow.
   */
  @Input() pattern = '^\\d{1,2}/\\d{1,2}/\\d{4}$';

  /**
   * Date picker id.
   */
  @Input() id = `datepicker-${DatePicker.datePickerCount++}`;

  /**
   * Sets the value.
   */
  @Input() set value(v: (Date | string)[]) {
    if (!v) {
      v = [];
    }
    this._value = v;
  }

  get value() {
    return this._value;
  }

  /**
   * Set to `true` to disable the `Datepicker`
   */
  @Input() disabled = false;

  /**
   * Set to `true` to display invalid state.
   */
  @Input() invalid = false;

  /**
   * Feedback message to display when `Datepicker` is invalid.
   */
  @Input() invalidText: string | TemplateRef<any>;

  /**
   * Skeleton state.
   */
  @Input() skeleton = false;

  /**
   * Array of date picker plugins.
   */
  @Input() plugins = [];

  /**
   * Flatpickr options
   */
  @Input()
  set flatpickrOptions(options) {
    this._flatpickrOptions = Object.assign({}, this._flatpickrOptions, options);
  }
  get flatpickrOptions() {
    const plugins = [...this.plugins, sukaFlatpickrMonthSelectPlugin];
    if (this.range) {
      plugins.push(rangePlugin({ input: `#${this.id}-rangeInput`, position: 'left' }));
    }
    return Object.assign({}, this._flatpickrOptions, this.flatpickrBaseOptions, {
      mode: this.range ? 'range' : 'single',
      plugins,
      dateFormat: this.dateFormat,
      enableTime: this.hasTime,
      noCalendar: this.noCalendar,
    });
  }

  /**
   * Emits event notifying other classes of a change.
   */
  @Output() valueChange: EventEmitter<any> = new EventEmitter();

  protected _value = [];

  protected _flatpickrOptions = {
    allowInput: true
  };

  protected flatpickrBaseOptions = {
    mode: 'single',
    dateFormat: 'd/m/Y',
    locale: Spanish,
    plugins: this.plugins,
    onOpen: () => { this.updateClassNames(); },
    value: this.value
  };

  protected flatpickrInstance = null;

  constructor(protected elementRef: ElementRef) { }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isFlatpickrLoaded()) {
      let dates = this.flatpickrInstance.selectedDates;
      if (changes.value && this.didDateValueChange(changes.value.currentValue, changes.value.previousValue)) {
        dates = changes.value.currentValue;
      }
      // only reset the flatpickr instance on Input changes
      this.flatpickrInstance = flatpickr(`#${this.id}`, this.flatpickrOptions);
      this.setDateValues(dates);
    }
  }

  // because the actual view may be delayed in loading (think projection into a tab pane)
  // and because we rely on a library that operates outside the Angular view of the world
  // we need to keep trying to load the library, until the relevant DOM is actually live
  ngAfterViewChecked() {
    if (!this.isFlatpickrLoaded()) {
      this.flatpickrInstance = flatpickr(`#${this.id}`, this.flatpickrOptions);

      // if (and only if) the initialization succeeded, we can set the date values
      if (this.isFlatpickrLoaded()) {
        if (this.value.length > 0) {
          this.setDateValues(this.value);
        }
      }
    }
  }

  @HostListener('focusin')
  onFocus() {
    this.onTouched();
  }

  /**
   * Writes a value from the model to the component. Expects the value to be `null` or `(Date | string)[]`
   * @param value value received from the model
   */
  writeValue(value: (Date | string)[]) {
    this.value = value;
    if (this.isFlatpickrLoaded() && this.flatpickrInstance.config) {
      this.setDateValues(this.value);
    }
  }

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  onTouched: () => any = () => { };

  propagateChange = (_: any) => { };

  /**
   * Cleans up our flatpickr instance
   */
  ngOnDestroy() {
    if (!this.isFlatpickrLoaded()) { return; }
    this.flatpickrInstance.destroy();
  }

  /**
   * Handles the `valueChange` event from the primary/single input
   */
  onValueChange(event: string) {
    if (this.isFlatpickrLoaded()) {
      const date = this.flatpickrInstance.parseDate(event, this.dateFormat);
      if (this.range) {
        this.setDateValues([date, this.flatpickrInstance.selectedDates[1]]);
      } else {
        this.setDateValues([date]);
      }
      this.doSelect(this.flatpickrInstance.selectedDates);
    }
  }

  /**
   * Handles the `valueChange` event from the range input
   */
  onRangeValueChange(event: string) {
    if (this.isFlatpickrLoaded()) {
      const date = this.flatpickrInstance.parseDate(event, this.dateFormat);
      this.setDateValues([this.flatpickrInstance.selectedDates[0], date]);
      this.doSelect(this.flatpickrInstance.selectedDates);
    }
  }

  /**
   * Suka uses a number of specific classnames for parts of the flatpickr - this idempotent method applies them if needed.
   */
  protected updateClassNames() {
    if (!this.elementRef) { return; }

    // get all the possible flatpickrs in the document - we need to add classes to (potentially) all of them
    const calendarContainer = document.querySelectorAll('.flatpickr-calendar');
    const monthContainer = document.querySelectorAll('.flatpickr-month');
    const weekdaysContainer = document.querySelectorAll('.flatpickr-weekdays');
    const weekdayContainer = document.querySelectorAll('.flatpickr-weekday');
    const daysContainer = document.querySelectorAll('.flatpickr-days');
    const dayContainer = document.querySelectorAll('.flatpickr-day');

    // add classes to lists of elements
    const addClassIfNotExists = (classname: string, elementList: NodeListOf<Element>) => {
      Array.from(elementList).forEach(element => {
        if (!element.classList.contains(classname)) {
          element.classList.add(classname);
        }
      });
    };

    // add classes (but only if they don't exist, small perf win)
    addClassIfNotExists('date-picker__calendar', calendarContainer);
    addClassIfNotExists('date-picker__month', monthContainer);
    addClassIfNotExists('date-picker__weekdays', weekdaysContainer);
    addClassIfNotExists('date-picker__days', daysContainer);

    // add weekday classes and format the text
    Array.from(weekdayContainer).forEach(element => {
      element.innerHTML = element.innerHTML.replace(/\s+/g, '');
      element.classList.add('date-picker__weekday');
    });

    // add day classes and special case the "today" element based on `this.value`
    Array.from(dayContainer).forEach(element => {
      element.classList.add('date-picker__day');
      if (!this.value) {
        return;
      }
      if (element.classList.contains('today') && this.value.length > 0) {
        element.classList.add('no-border');
      } else if (element.classList.contains('today') && this.value.length === 0) {
        element.classList.remove('no-border');
      }
    });
  }

  /**
   * Applies the given date value array to both the flatpickr instance and the `input`(s)
   * @param dates the date values to apply
   */
  protected setDateValues(dates: (Date | string)[]) {
    if (this.isFlatpickrLoaded()) {
      const singleInput = this.elementRef.nativeElement.querySelector(`#${this.id}`);
      const rangeInput = this.elementRef.nativeElement.querySelector(`#${this.id}-rangeInput`);

      // set the date on the instance
      this.flatpickrInstance.setDate(dates);

      // we can either set a date value or an empty string, so we start with an empty string
      let singleDate = '';
      // if date is a string, parse and format
      if (typeof this.flatpickrInstance.selectedDates[0] === 'string') {
        singleDate = this.flatpickrInstance.parseDate(this.flatpickrInstance.selectedDates[0], this.dateFormat);
        singleDate = this.flatpickrInstance.formatDate(singleDate, this.dateFormat);
        // if date is not a string we can assume it's a Date and we should format
      } else if (!!this.flatpickrInstance.selectedDates[0]) {
        singleDate = this.flatpickrInstance.formatDate(this.flatpickrInstance.selectedDates[0], this.dateFormat);
      }

      if (rangeInput) {
        // we can either set a date value or an empty string, so we start with an empty string
        let rangeDate = '';
        // if date is a string, parse and format
        if (typeof this.flatpickrInstance.selectedDates[1] === 'string') {
          rangeDate = this.flatpickrInstance.parseDate(this.flatpickrInstance.selectedDates[1].toString(), this.dateFormat);
          rangeDate = this.flatpickrInstance.formatDate(rangeDate, this.dateFormat);
          // if date is not a string we can assume it's a Date and we should format
        } else if (!!this.flatpickrInstance.selectedDates[1]) {
          rangeDate = this.flatpickrInstance.formatDate(this.flatpickrInstance.selectedDates[1], this.dateFormat);
        }
        setTimeout(() => {
          // apply the values
          rangeInput.value = rangeDate;
          singleInput.value = singleDate;
        });
      }
    }
  }

  protected doSelect(selectedValue: (Date | string)[]) {
    this.valueChange.emit(selectedValue);
    this.propagateChange(selectedValue);
  }

  protected didDateValueChange(currentValue, previousValue) {
    return currentValue[0] !== previousValue[0] || currentValue[1] !== previousValue[1];
  }

  /**
   * More advanced checking of the loaded state of flatpickr
   */
  protected isFlatpickrLoaded() {
    // cast the instance to a boolean, and some method that has to exist for the library to be loaded in this case `setDate`
    return !!this.flatpickrInstance && !!this.flatpickrInstance.setDate;
  }
}
